import { FeedbackModelRecord } from "lib/services/catalogical-models";

const CACHE_KEY_PREFIX = "cached_review_";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedReview {
  review: FeedbackModelRecord;
  cachedAt: number;
}

const getKey = (taxonomyId: string): string => `${CACHE_KEY_PREFIX}${taxonomyId}`;

export const cacheReview = (taxonomyId: string, review: FeedbackModelRecord): void => {
  try {
    const entry: CachedReview = { review, cachedAt: Date.now() };
    localStorage.setItem(getKey(taxonomyId), JSON.stringify(entry));
  } catch {
    // Storage full or unavailable — silently fail
  }
};

export const getCachedReview = (taxonomyId: string): FeedbackModelRecord | null => {
  try {
    const raw = localStorage.getItem(getKey(taxonomyId));
    if (!raw) return null;

    const entry: CachedReview = JSON.parse(raw);
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(getKey(taxonomyId));
      return null;
    }
    return entry.review;
  } catch {
    return null;
  }
};

export const clearCachedReview = (taxonomyId: string): void => {
  try {
    localStorage.removeItem(getKey(taxonomyId));
  } catch {
    // silently fail
  }
};

/**
 * Deep compares the relevant fields of a cached review against a backend review
 * to determine if the backend has indexed the latest version.
 */
export const isReviewMatchingCache = (cached: FeedbackModelRecord, backend: FeedbackModelRecord): boolean =>
  cached.rating === backend.rating &&
  cached.title === backend.title &&
  cached.content === backend.content &&
  JSON.stringify(cached.categories?.slice().sort()) === JSON.stringify(backend.categories?.slice().sort());
